import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import { GlobalFilter } from '../../interfaces/global-filter-model';
import { MatOption } from '@angular/material/core';
import { GlobalFilterService } from '../../services/global-filter.service';

interface FilterField {
  formControl: FormControl;
  filterCtrl: FormControl;
  filtered: ReplaySubject<any[]>;
  values: any[];
}

@Component({
  selector: 'app-global-filter',
  templateUrl: './global-filter.component.html',
  styleUrls: ['./global-filter.component.scss'],
})

export class GlobalFilterComponent implements OnInit, OnDestroy {
  @ViewChild('allSelectedPlant') public allSelectedPlant: MatOption;
  @ViewChild('allSelectedCustomer') public allSelectedCustomer: MatOption;
  @ViewChild('allSelectedPartNo') public allSelectedPartNo: MatOption;
  @ViewChild('allSelectedOrderTypes') public allSelectedOrderTypes: MatOption;
  @ViewChild('allSelectedWarehouse') public allSelectedWarehouse: MatOption;
  @ViewChild('allSelectedClassification') public allSelectedClassification: MatOption;
  @ViewChild('allSelectedDaterange') public allSelectedDaterange: MatOption;

  @Input() filterVisibility: {
    plant?: boolean,
    sales_channel_code?: boolean,
    part_no?: boolean,
    order_types?: boolean,
    warehouse?: boolean,
    classification?: boolean,
    date_range?: boolean,
  } = {};

  private subscriptions = new Subscription();
  private destroy$ = new Subject<void>();

  public filterForm: FormGroup;
  public currentFilter: any;
  public initialFiltersValues: any = {};
  public filterIconFlag: any = {};
  public filterMasterData: any = [];
  public masterData: any;
  public filterCount: number = 0;
  public globalFilters: { [key: string]: FilterField } = {};
  private filterFields = ['sc_node_code', 'sales_channel_code', 'sku_code', 'norm_type', 'demand_node_code', 'classification', 'date_range'];

  constructor(
    private fb: FormBuilder,
    private globalFilterService: GlobalFilterService
  ) { }

  ngOnInit(): void {
    this.globalFilterService.loadFilterData();
    this.initializeGlobalFilters();
    this.currentFilter = this.globalFilterService.currentFilters;
    this.createForm();
    this.initializeValues();

    Object.keys(this.globalFilters).forEach(key => {
      this.globalFilters[key].filterCtrl.valueChanges.subscribe(() => {
        this.filterFilters(key, this.globalFilters);
      });
    });
  }

  private initializeGlobalFilters() {
    this.filterFields.forEach((field) => {
      this.globalFilters[field] = {
        formControl: new FormControl(null),
        filterCtrl: new FormControl(null),
        filtered: new ReplaySubject<any[]>(1),
        values: [],
      };
      this.globalFilters[field].filtered.next([]);
    });
  }

  public createForm() {
    this.filterForm = this.fb.group({
      sc_node_code: [null],
      sales_channel_code: [null],
      sku_code: [null],
      norm_type: [null],
      demand_node_code: [null],
      classification: [null],
      date_range: [null]
    });
  }

  initializeValues() {
    this.subscriptions.add(
      this.globalFilterService.filterMasterData.subscribe({
        next: (data) => {
          if (data) {
            this.masterData = data;
            this.filterMasterData = data['data'];

            // 💡 Set norm_type immediately
            if (this.masterData?.norm_type?.length) {
              const normList = this.masterData.norm_type;
              this.globalFilters['norm_type'].values = normList;
              this.globalFilters['norm_type'].filtered.next(normList);
            }

            if (this.masterData?.date_range?.length) {
              const dateList = this.masterData.date_range.map(item => item.week_str);
              this.globalFilters['date_range'].values = dateList;
              this.globalFilters['date_range'].filtered.next(dateList);
            }

            setTimeout(() => {
              this.initFilterData(this.filterMasterData, '', this.globalFilters);

              if (this.currentFilter) {
                this.filterForm.patchValue({ ...this.currentFilter });
              }

              this.globalFilterService.updateDaterange(this.masterData.date_range);
              this.globalFilterService.updateGlobalFiltersSelection(this.filterForm.value);
              this.filterCount = this.calculateFilterCount();

            }, 0);
          }
        },
      })
    );

    this.subscriptions.add(
      this.globalFilterService.globalFilters.subscribe((value: GlobalFilter) => {
        if (value) {
          this.filterForm.patchValue({ ...value });
          this.applyFilter();
        }
      })
    );
  }

  handleOpenedChange(isOpened, formFiled) {
    this.filterIconFlag[formFiled] = isOpened;
    if (isOpened) {
      this.setInitialFilterFormValues(formFiled);
    } else {
      if (this.initialFiltersValues.hasOwnProperty(formFiled)) {
        const initialValue = this.initialFiltersValues[formFiled];
        const currentValue = this.filterForm.controls[formFiled].value;
        if ((initialValue === null && (currentValue === null || (Array.isArray(currentValue) && currentValue.length === 0))) === false) {
          const valueChanged = this.isValueChanged(initialValue, currentValue);
          if (valueChanged) {
            this.applyFilter();
            this.globalFilterService.updateGlobalFiltersSelection(this.filterForm.value);
            this.filterCount = this.calculateFilterCount();
          }
        }
      }
    }
  }

  setInitialFilterFormValues(formFiled) {
    this.initialFiltersValues[formFiled] = this.filterForm.controls[formFiled].value;
  }

  isValueChanged(initialValue: any, currentValue: any): boolean {
    if (Array.isArray(initialValue) && Array.isArray(currentValue)) {
      if (initialValue.length !== currentValue.length) {
        return true;
      } else {
        return !initialValue.every((element) => {
          if (currentValue.includes(element)) {
            return true;
          }
          return false;
        });
      }
    } else {
      return initialValue !== currentValue;
    }
  }

  protected filterFilters(field, filters) {
    const search = filters[field].filterCtrl.value;

    if (!search) {
      const anyFilterActive = this.filterFields.some((f) => {
        const val = this.filterForm.get(f)?.value;
        return Array.isArray(val) ? val.length > 0 : !!val;
      });

      if (!anyFilterActive) {
        filters[field].filtered.next(filters[field].values.slice());
      }

      return;
    }

    const lowerSearch = search.toLowerCase();
    filters[field].filtered.next(
      filters[field].values.filter((el) =>
        el?.toLowerCase().includes(lowerSearch)
      )
    );
  }

  public initFilterData(data: any[], formField: string, filters: any): void {
    const keys = Object.keys(filters);
    const fieldSets: Record<string, Set<string>> = {};

    // Step 1: Collect unique values from data for all filters (excluding norm_type and date_range)
    for (const item of data) {
      for (const key of keys) {
        if (key === formField || key === 'date_range' || key === 'norm_type') continue;

        if (!fieldSets[key]) {
          fieldSets[key] = new Set<string>();
        }

        fieldSets[key].add(item[key]);
      }
    }

    // Step 2: Assign values to filters (skip norm_type and date_range)
    for (const key of keys) {
      if (key === formField || key === 'norm_type' || key === 'date_range') continue;

      const dataList = fieldSets[key] ? Array.from(fieldSets[key]) : [];
      filters[key].values = dataList;
      filters[key].filtered.next(dataList);
    }
  }

  applyFilter() {
    const excludeFields = ['date_range', 'norm_type'];
    const selectedFilterFields = this.filterFields.filter(field => !excludeFields.includes(field));

    const filters = selectedFilterFields.map((key) => ({
      key,
      values: this.filterForm.value[key] || []
    }));

    const masterDataCopy = [...this.filterMasterData];

    selectedFilterFields.forEach((key) => {
      const filteredData = masterDataCopy.filter((item) =>
        filters.every((f) => f.key === key || f.values.length === 0 || f.values.includes(item[f.key]))
      );
      const uniqueValues = [...new Set(filteredData.map(item => item[key]))].sort();
      this.globalFilters[key].filtered.next(uniqueValues);
    });
  }

  getkeysfilter(key, data) {
    return data.map((ele) => ele[key]);
  }

  toggleAllSelection(formField: string, alloptions: MatOption) {
    const control = this.filterForm.get(formField);
    const allOptions = this.globalFilters[formField].values || [];

    if (!control) return;

    if (this.isAllSelected(formField)) {
      control.patchValue([]);
      alloptions.deselect();
    } else {
      control.patchValue([...allOptions, '__all__']);
      alloptions.select();
    }
  }

  togglePerOne(formField: string, alloptions: MatOption) {
    const control = this.filterForm.get(formField);
    const allOptions = this.globalFilters[formField].values || [];

    if (!control) return;

    const selected = control.value || [];
    const realSelected = selected.filter(v => v !== '__all__');

    if (realSelected.length === allOptions.length) {
      control.patchValue([...allOptions, '__all__']);
      alloptions.select();
    } else {
      control.patchValue(realSelected);
      alloptions.deselect();
    }
  }

  isAllSelected(formField: string): boolean {
    const control = this.filterForm.get(formField);
    const allOptions = this.globalFilters[formField].values || [];
    if (!control) return false;

    const selected = control.value || [];
    const realSelected = selected.filter(v => v !== '__all__');

    return realSelected.length === allOptions.length;
  }

  onClearFilter() {
    this.filterForm.reset();
    this.globalFilterService.updateGlobalFiltersSelection(this.filterForm.value);
    this.filterCount = this.calculateFilterCount();
    this.initFilterData(this.filterMasterData, '', this.globalFilters);
  }

  calculateFilterCount(): number {
    const filters = this.filterForm.value;
    const filtersCount = Object.keys(filters).reduce((count, key) => {
      if (filters[key] && filters[key].length > 0) {
        return count + 1;
      }
      return count;
    }, 0);

    return filtersCount;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.globalFilterService.resetFilters();
  }
}
